import React, { useState, useEffect, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MuiAutocomplete from "@mui/material/Autocomplete";
import ConfirmDeleteModal from "../../components/images/ConfirmDeleteModal";
import { useProject } from "../../contexts/project";
import useUpdateProjectSettings from "../../hooks/useUpdateProjectSettings";
import { FIELD_ORDER_SECTIONS, getFieldLabel } from "../../utils/fieldOrder";
import FieldReorderList from "../../components/capture/FieldReorderList";

export default function FieldsReorderModal({
  open,
  onClose,
  fields,
  selectedSectionId,
}) {
  const currentProject = useProject();
  const [updateProjectSettings] = useUpdateProjectSettings();
  const [orderedFields, setOrderedFields] = useState(fields);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedField, setSelectedField] = useState(null);
  const dialogContentRef = useRef(null);

  useEffect(() => {
    setOrderedFields(fields);
  }, [fields]);

  useEffect(() => {
    if (!open) return;

    window.requestAnimationFrame(() => {
      const container = dialogContentRef.current;
      if (!container) return;
      container.scrollTop = 0;
    });
  }, [open, fields, selectedSectionId]);
  useEffect(() => {
    if (!search) return;
    const match = searchResults[0];
    if (!match) return;
    dialogContentRef.current
      ?.querySelector(`[data-field-id="${match.field.id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [search]);

  const handleSave = () => {
    if (!currentProject?.id) {
      onClose();
      return;
    }

    const sectionsData = {};
    FIELD_ORDER_SECTIONS.forEach(section => {
      const sectionFields = orderedFields[section.id] || [];
      sectionsData[section.id] = sectionFields.map(f => f.id);
    });

    const fieldOrder = {
      version: 1,
      sections: sectionsData,
    };

    const existingFieldSettings = currentProject?.fieldSettings ?? {};

    updateProjectSettings({
      variables: {
        projectId: currentProject?.id,
        fieldSettings: {
          ...existingFieldSettings,
          fieldOrder,
        },
      },
    });
    onClose();
  };

  const handleResetToDefault = () => {
    setResetConfirmationOpen(true);
  };

  const confirmReset = async () => {
    if (!currentProject?.id) {
      setResetConfirmationOpen(false);
      onClose();
      return;
    }

    const existingFieldSettings = currentProject.fieldSettings ?? {};

    const { fieldOrder, ...restSettings } = existingFieldSettings;
    await updateProjectSettings({
      variables: {
        projectId: currentProject?.id,
        fieldSettings: restSettings,
      },
    });
    setResetConfirmationOpen(false);
    onClose();
  };

  const visibleSections = FIELD_ORDER_SECTIONS.filter(
    section => (orderedFields[section.id] || []).length > 0
  );
  const searchResults = Object.entries(orderedFields).flatMap(([section, sectionFields]) =>
    sectionFields
      .filter(field => getFieldLabel(field).toLowerCase().includes(search.toLowerCase()))
      .map(field => ({ field, section }))
  );
  const moveToTop = destinationSection => {
    const { field, section: sourceSection } = selectedField;
    const nextFields = Object.fromEntries(
      Object.entries(orderedFields).map(([section, sectionFields]) => [section, [...sectionFields]])
    );
    nextFields[sourceSection] = nextFields[sourceSection].filter(item => item.id !== field.id);
    nextFields[destinationSection] = [{ ...field, section: destinationSection }, ...(nextFields[destinationSection] || [])];
    setOrderedFields(nextFields);
    setSelectedField(null);
    setSearch("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", color: "text.primary" }}>
        <Typography color="text.primary" fontWeight={500} fontSize={20}>
          Reorder Fields
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        ref={dialogContentRef}
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 3 }}
      >
        <Box>
          <MuiAutocomplete
            fullWidth
            size="small"
            options={Object.entries(orderedFields)
              .flatMap(([section, sectionFields]) =>
                sectionFields.map(field => ({ field, section }))
              )
              .sort((a, b) => getFieldLabel(a.field).localeCompare(getFieldLabel(b.field)))}
            getOptionLabel={({ field }) => getFieldLabel(field)}
            inputValue={search}
            onInputChange={(_event, value) => setSearch(value)}
            onChange={(_event, selection) => {
              if (!selection) return;
              setSearch(getFieldLabel(selection.field));
            }}
            renderInput={params => <TextField {...params} label="Search fields" />}
          />
        </Box>
        <FieldReorderList
          orderedFields={orderedFields}
          onOrderChange={setOrderedFields}
          sections={visibleSections}
          highlightedFieldId={search ? searchResults[0]?.field.id : undefined}
          onMoveToTop={(field, section) => setSelectedField({ field, section })}
        />
      </DialogContent>
      <DialogActions
        sx={{ px: 3, pb: 2, backgroundColor: "white", justifyContent: "space-between" }}
      >
        <Button variant="text" color="error" onClick={handleResetToDefault}>
          Reset to Default
        </Button>
        <Box display="flex" gap={1}>
          <Button variant="outlined" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </DialogActions>
      {resetConfirmationOpen && (
        <ConfirmDeleteModal
          title="Reset to Default"
          descr="Are you sure you want to reset the fields order to default?"
          onClose={() => setResetConfirmationOpen(false)}
          onDelete={confirmReset}
          applyLabel="Reset"
        />
      )}
      {selectedField && (
        <Dialog open onClose={() => setSelectedField(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: "center", py: 2 }}>
            <Typography
              align="center"
              color="text.primary"
              fontWeight={500}
              sx={{ fontSize: "18px !important" }}
            >
              Reorder {getFieldLabel(selectedField.field)}
            </Typography>
          </DialogTitle>
          <DialogActions sx={{ px: 3, pb: 2, flexDirection: "column", alignItems: "stretch", gap: 1 }}>
            {visibleSections.map(section => (
              <Button
                key={section.id}
                variant={section.id === selectedField.section ? "contained" : "outlined"}
                onClick={() => moveToTop(section.id)}
              >
                Top of {section.title}
              </Button>
            ))}
          </DialogActions>
        </Dialog>
      )}
    </Dialog>
  );
}
